// ─────────────────────────────────────────────────────────────────────────────
// V2Recurring — /dashboard/recurring-invoices in the v2 design.
// Endpoints are v1's: GET/POST /api/recurring-invoices, GET/PUT/DELETE /:id,
// PATCH /:id/status, POST /:id/run-now. Payload identical to v1 (formData + items).
// Products come from GET /api/products (v1 called /api/products/fetchallproducts,
// which doesn't exist, so v1's product picker was always empty).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Money, Segmented, Sheet, Skel, Switch, inr } from "./primitives";
import { Combo, Field, LineItems, PageHead, Stat, newLine, useClients, useProducts } from "./kit";

const BASE = `${API_BASE}/api/recurring-invoices`;
const auth = () => ({ "auth-token": localStorage.getItem("token") });
const json = () => ({ "Content-Type": "application/json", ...auth() });
const today = () => new Date().toISOString().split("T")[0];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const clientOf = (s) => s.clientName || s.clientId?.clientName || "Client";
const FREQ = { weekly: "Every week", monthly: "Every month", quarterly: "Every quarter", yearly: "Every year" };

// Exact port of v1 calcTotals()
function calcTotals(items, discountPercent) {
  let subtotal = 0;
  items.forEach((it) => { subtotal += Number(it.price || 0) * Number(it.qty || 0); });
  const pct = Math.min(Math.max(Number(discountPercent || 0), 0), 100);
  const discountAmount = (subtotal * pct) / 100;
  const taxableAmount = subtotal - discountAmount;
  let totalGst = 0;
  items.forEach((it) => {
    const s = Number(it.price || 0) * Number(it.qty || 0);
    const t = subtotal > 0 ? (s / subtotal) * taxableAmount : 0;
    totalGst += (t * Number(it.gstRate || 0)) / 100;
  });
  return { subtotal, discountAmount, taxableAmount, totalGst, grandTotal: taxableAmount + totalGst };
}

const relDays = (d) => {
  if (!d) return "";
  const days = Math.round((new Date(d).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  if (days < 0) return `${-days} day${days === -1 ? "" : "s"} overdue`;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
};

// ══════════════════════════════════════════════════════════════════════════════
export default function V2Recurring({ showAlert }) {
  usePageTitle("Recurring Invoices");
  const navigate = useNavigate();
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("invoices", "write");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [freq, setFreq] = useState("ALL");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);   // null | "new" | schedule
  const [confirm, setConfirm] = useState(null);   // { kind: "run"|"delete", s }
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [busy, setBusy] = useState("");
  const newBtn = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(BASE, { headers: auth() });
      const d = r.ok ? await r.json() : [];
      setList(Array.isArray(d) ? d : d.schedules || []);
    } catch { setList([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const kpi = {
    active: list.filter((s) => s.status === "active").length,
    paused: list.filter((s) => s.status === "paused").length,
    generated: list.reduce((n, s) => n + (s.occurrencesCount || 0), 0),
    next: list.filter((s) => s.status === "active" && s.nextRunDate).sort((a, b) => new Date(a.nextRunDate) - new Date(b.nextRunDate))[0],
    monthly: list.filter((s) => s.status === "active").reduce((sum, s) => {
      const per = calcTotals(s.items || [], s.discountPercent).grandTotal;
      return sum + per * ({ weekly: 52 / 12, monthly: 1, quarterly: 1 / 3, yearly: 1 / 12 }[s.frequency] || 0);
    }, 0),
  };

  const shown = list.filter((s) => (status === "ALL" || s.status === status) && (freq === "ALL" || s.frequency === freq) &&
    (!q.trim() || clientOf(s).toLowerCase().includes(q.trim().toLowerCase())));

  const setSched = async (s, next) => {
    setBusy(`st:${s._id}`);
    try {
      const r = await fetch(`${BASE}/${s._id}/status`, { method: "PATCH", headers: json(), body: JSON.stringify({ status: next }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't update");
      showAlert?.(next === "active" ? "Schedule resumed" : "Schedule paused", "success");
      load();
    } catch (e) { showAlert?.(e.message, "danger"); }
    finally { setBusy(""); }
  };

  const runConfirm = async () => {
    const { kind, s } = confirm;
    setBusy("confirm");
    try {
      if (kind === "run") {
        const r = await fetch(`${BASE}/${s._id}/run-now`, { method: "POST", headers: auth() });
        const d = await r.json().catch(() => ({}));
        if (!r.ok || !d.success) throw new Error(d.error || "Couldn't generate the invoice");
        showAlert?.(`Invoice ${d.invoiceNumber} generated`, "success");
      } else {
        const r = await fetch(`${BASE}/${s._id}`, { method: "DELETE", headers: auth() });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || "Couldn't delete");
        showAlert?.("Schedule deleted", "success");
      }
      setConfirm(null); setExpanded(null); load();
    } catch (e) { setConfirm((c) => (c ? { ...c, error: e.message } : c)); }
    finally { setBusy(""); }
  };

  const toggle = async (s) => {
    if (expanded === s._id) { setExpanded(null); return; }
    setExpanded(s._id);
    if (!detail[s._id]) {
      try {
        const r = await fetch(`${BASE}/${s._id}`, { headers: auth() });
        const d = await r.json();
        setDetail((p) => ({ ...p, [s._id]: d }));
      } catch { /* silent */ }
    }
  };

  return (
    <div className="v2p v2r">
      <PageHead eyebrow="Receivables" title="Recurring invoices" sub="Invoices that create themselves — every morning at 6 AM IST."
        actions={canWrite && <button ref={newBtn} type="button" className="v2-btn v2-btn--primary" onClick={() => setEditing("new")}><Glyph name="plus" size={17} stroke={2.2} /> New schedule</button>} />

      <div className="v2k-stats">
        <Stat label="Active" value={loading ? "—" : kpi.active} note={kpi.paused ? `${kpi.paused} paused` : "None paused"} />
        <Stat label="Billing per month" value={loading ? "—" : inr(kpi.monthly)} note="From active schedules, incl. GST" />
        <Stat label="Invoices generated" value={loading ? "—" : kpi.generated} note="All time" />
        <Stat label="Next run" value={kpi.next ? fmtDate(kpi.next.nextRunDate) : "—"} note={kpi.next ? `${clientOf(kpi.next)} · ${relDays(kpi.next.nextRunDate)}` : "Nothing scheduled"} />
      </div>

      <div className="v2i-toolbar">
        <Segmented ariaLabel="Status" value={status} onChange={setStatus}
          options={[{ value: "ALL", label: "All" }, { value: "active", label: "Active" }, { value: "paused", label: "Paused" }, { value: "completed", label: "Done" }]} />
        <Segmented size="sm" ariaLabel="Frequency" value={freq} onChange={setFreq}
          options={[{ value: "ALL", label: "Any" }, { value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" }, { value: "yearly", label: "Yearly" }]} />
        <div className="v2-field--search v2i-search">
          <Glyph name="search" size={17} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client" aria-label="Search schedules" />
        </div>
      </div>

      {loading ? <div className="v2r-grid">{[0, 1, 2].map((i) => <Skel key={i} h={210} r={26} />)}</div>
        : shown.length === 0 ? (
          <section className="v2-card"><div className="v2c-empty"><Duo name="repeat" size={48} /><strong>{list.length ? "Nothing in this view" : "No schedules yet"}</strong>
            <span>Set up retainers, rent or subscriptions once — EazyBilling issues and emails each invoice on time.</span>
            {canWrite && !list.length && <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" onClick={() => setEditing("new")}>New schedule</button>}</div></section>
        ) : (
          <div className="v2r-grid">
            {shown.map((s) => {
              const per = calcTotals(s.items || [], s.discountPercent).grandTotal;
              const max = s.maxOccurrences ? Number(s.maxOccurrences) : null;
              const pct = max ? Math.min(100, ((s.occurrencesCount || 0) / max) * 100) : null;
              const d = detail[s._id];
              const open = expanded === s._id;
              return (
                <article key={s._id} className={`v2-card v2r-card v2r-card--${s.status}${open ? " is-open" : ""}`}>
                  <div className="v2r-top">
                    <span className="v2-avatar v2-avatar--client" style={{ width: 42, height: 42, fontSize: 15 }}>{clientOf(s).trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}</span>
                    <span className="v2-row-main">
                      <span className="v2-row-title">{clientOf(s)}</span>
                      <span className="v2-row-sub">{FREQ[s.frequency] || s.frequency} · {s.invoicePrefix || "REC"}-…</span>
                    </span>
                    {canWrite && (
                      <MenuButton className="v2-icon-btn v2i-more" label="Schedule options" width={230} items={[
                        ...(s.status === "active" ? [{ label: "Run now", glyph: "sparkle", onSelect: () => setConfirm({ kind: "run", s }) }] : []),
                        { label: "Edit schedule", glyph: "note", onSelect: () => setEditing(s) },
                        ...(s.status === "active" ? [{ label: "Pause", glyph: "clock", onSelect: () => setSched(s, "paused") }] : []),
                        ...(s.status === "paused" ? [{ label: "Resume", glyph: "repeat", onSelect: () => setSched(s, "active") }] : []),
                        "divider",
                        { label: "Delete schedule", glyph: "close", danger: true, onSelect: () => setConfirm({ kind: "delete", s }) },
                      ]} />
                    )}
                  </div>
                  <div className="v2r-amt"><Money value={per} /><span>per invoice</span></div>
                  <div className="v2r-next">
                    {s.status === "active" ? (
                      <><Glyph name="calendar" size={15} /><span>Next <b>{fmtDate(s.nextRunDate)}</b> · {relDays(s.nextRunDate)}</span></>
                    ) : s.status === "paused" ? (
                      <span className="v2-status v2-status--warn"><Glyph name="clock" size={13} stroke={2.2} /> Paused</span>
                    ) : (
                      <span className="v2-status v2-status--good"><Glyph name="check" size={13} stroke={2.2} /> {s.status === "completed" ? "Completed" : s.status}</span>
                    )}
                    {s.autoSendEmail !== false && <span className="v2r-mail" title="Emailed automatically"><Glyph name="bell" size={13} /> Auto-email</span>}
                  </div>
                  <div className="v2r-progress">
                    <div className="v2r-bar"><span style={{ width: `${pct ?? 100}%` }} className={pct === null ? "is-open-ended" : ""} /></div>
                    <span>{s.occurrencesCount || 0}{max ? ` of ${max}` : ""} sent{s.endDate ? ` · ends ${fmtDate(s.endDate)}` : max ? "" : " · no end date"}</span>
                  </div>
                  {s.lastRunError && <div className="v2-banner v2-banner--bad v2r-err"><Glyph name="alert" size={15} /> Last run failed: {s.lastRunError}</div>}
                  {busy === `st:${s._id}` && <div className="v2r-busy"><span className="v2-spin" /></div>}
                  <button type="button" className="v2r-more" aria-expanded={open} onClick={() => toggle(s)}>
                    {open ? "Hide history" : "History & details"} <Glyph name="chevronDown" size={15} className="v2i-chev" />
                  </button>
                  <div className="v2i-collapse"><div>
                    {open && !d ? <Skel h={80} r={14} /> : d && (
                      <div className="v2r-detail">
                        <dl className="v2pub-meta">
                          <div><dt>Started</dt><dd>{fmtDate(d.startDate)}</dd></div>
                          <div><dt>Last run</dt><dd>{fmtDate(d.lastRunDate)}</dd></div>
                          <div><dt>Terms</dt><dd>{d.paymentTimeline || 30} days</dd></div>
                          <div><dt>Tax</dt><dd>{d.taxType === "IGST" ? "IGST" : "CGST+SGST"}{d.discountPercent ? ` · ${d.discountPercent}% off` : ""}</dd></div>
                        </dl>
                        {(d.recentInvoices || []).length > 0 ? (
                          <div className="v2-inset">
                            {d.recentInvoices.map((inv) => (
                              <button key={inv._id} type="button" className="v2-inset-row" onClick={() => navigate(`/dashboard/invoice/${inv._id}`)}>
                                <span className="v2-row-main"><span className="v2-row-title">{inv.invoiceNumber}</span><span className="v2-row-sub">{fmtDate(inv.invoiceDate)}</span></span>
                                <b>{inr(inv.grandTotal, { decimals: 2 })}</b>
                                <Glyph name="chevronRight" size={15} className="v2-inset-chev" />
                              </button>
                            ))}
                          </div>
                        ) : <p className="v2-row-sub">No invoices generated yet.</p>}
                      </div>
                    )}
                  </div></div>
                </article>
              );
            })}
          </div>
        )}

      <Editor value={editing} originRef={newBtn} onClose={() => setEditing(null)}
        onSaved={(msg) => { setEditing(null); showAlert?.(msg, "success"); setDetail({}); load(); }} />

      <Sheet open={!!confirm} onClose={() => setConfirm(null)} width={440}
        title={confirm?.kind === "run" ? `Generate an invoice for ${confirm && clientOf(confirm.s)} now?` : "Delete this schedule?"}
        footer={<><button type="button" className="v2-btn" onClick={() => setConfirm(null)}>Not now</button>
          <button type="button" className={`v2-btn ${confirm?.kind === "delete" ? "v2c-btn-danger" : "v2-btn--primary"}`} disabled={busy === "confirm"} onClick={runConfirm}>
            {busy === "confirm" ? "Working…" : confirm?.kind === "run" ? "Generate invoice" : "Delete schedule"}</button></>}>
        <p className="v2c-remove-text">
          {confirm?.kind === "run"
            ? `A real invoice is created immediately (and emailed if auto-email is on). The next scheduled date moves forward as usual.`
            : `No further invoices will be generated. Invoices already issued from this schedule are not affected.`}
        </p>
        {confirm?.error && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {confirm.error}</div>}
      </Sheet>
    </div>
  );
}

// ── Create / edit ────────────────────────────────────────────────────────────
const EMPTY = { clientId: "", frequency: "monthly", startDate: today(), endDate: "", maxOccurrences: "", invoicePrefix: "REC", paymentTimeline: 30, taxType: "CGST_SGST", discountPercent: 0, autoSendEmail: true, notes: "" };

function Editor({ value, originRef, onClose, onSaved }) {
  const open = !!value;
  const isNew = value === "new";
  const { clients } = useClients();
  const { products, loading: pLoading } = useProducts();
  const [f, setF] = useState(EMPTY);
  const [ends, setEnds] = useState("never");
  const [lines, setLines] = useState(() => [newLine({ gstRate: 0, unit: "" })]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const was = useRef(null);

  useEffect(() => {
    if (!open || was.current === value) return;
    was.current = value;
    setErr("");
    if (isNew) { setF({ ...EMPTY, startDate: today() }); setEnds("never"); setLines([newLine({ gstRate: 0, unit: "" })]); return; }
    const s = value;
    setF({
      clientId: (s.clientId && (s.clientId._id || s.clientId)) || "", frequency: s.frequency || "monthly",
      startDate: s.startDate ? new Date(s.startDate).toISOString().split("T")[0] : today(),
      endDate: s.endDate ? new Date(s.endDate).toISOString().split("T")[0] : "", maxOccurrences: s.maxOccurrences || "",
      invoicePrefix: s.invoicePrefix || "REC", paymentTimeline: s.paymentTimeline || 30, taxType: s.taxType || "CGST_SGST",
      discountPercent: s.discountPercent || 0, autoSendEmail: s.autoSendEmail !== false, notes: s.notes || "",
    });
    setEnds(s.endDate ? "date" : s.maxOccurrences ? "count" : "never");
    const items = (s.items || []).map((it) => newLine({ productId: it.productId || null, custom: !it.productId, name: it.name || "", hsnCode: it.hsnCode || "", price: it.price || "", qty: it.qty || 1, gstRate: it.gstRate || 0, unit: it.unit || "" }));
    setLines(items.length ? items : [newLine({ gstRate: 0, unit: "" })]);
  }, [open, value]); // eslint-disable-line
  useEffect(() => { if (!open) was.current = null; }, [open]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
  const items = lines.filter((l) => l.productId || l.name?.trim());
  const preview = useMemo(() => calcTotals(items, f.discountPercent), [JSON.stringify(items), f.discountPercent]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.clientId) { setErr("Choose a client."); return; }
    if (!items.length) { setErr("Add at least one item."); return; }
    for (const it of items) {
      if (!it.name) { setErr("Every item needs a name."); return; }
      if (Number(it.qty) <= 0) { setErr(`Invalid quantity for ${it.name}.`); return; }
    }
    const payload = {
      ...f,
      endDate: ends === "date" ? f.endDate : "",
      maxOccurrences: ends === "count" ? f.maxOccurrences : "",
      items: items.map((it) => ({ productId: it.productId || null, name: it.name, hsnCode: it.hsnCode || "", price: parseFloat(it.price || 0), qty: parseFloat(it.qty || 1), gstRate: parseFloat(it.gstRate || 0), unit: it.unit || "" })),
    };
    setBusy(true);
    try {
      const r = await fetch(isNew ? BASE : `${BASE}/${value._id}`, { method: isNew ? "POST" : "PUT", headers: json(), body: JSON.stringify(payload) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.error || "Couldn't save the schedule."); return; }
      onSaved(isNew ? "Schedule created" : "Schedule updated");
    } catch { setErr("Network error — nothing was saved."); }
    finally { setBusy(false); }
  };

  const clientItems = clients.map((c) => ({ id: c._id, label: c.clientName, sub: c.clientEmail || c.clientGSTIN || "" }));
  const client = clients.find((c) => c._id === f.clientId);

  return (
    <Sheet open={open} onClose={onClose} originRef={isNew ? originRef : undefined} width={760}
      title={isNew ? "New recurring schedule" : "Edit schedule"} subtitle={client ? client.clientName : "Bill a client automatically"}
      footer={<>
        <span className="v2cn-foot-total">Each invoice <b>{inr(preview.grandTotal, { decimals: 2 })}</b></span>
        <button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="submit" form="v2r-form" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Saving…" : isNew ? "Create schedule" : "Save changes"}</button>
      </>}>
      <form id="v2r-form" className="v2-form" onSubmit={submit}>
        <Field label="Client"><Combo items={clientItems} value={f.clientId} onChange={(id) => setF((p) => ({ ...p, clientId: id }))} placeholder="Choose a client" searchPlaceholder="Search clients" ariaLabel="Client" /></Field>
        <Field label="Repeats">
          <Segmented ariaLabel="Frequency" value={f.frequency} onChange={(v) => setF((p) => ({ ...p, frequency: v }))}
            options={[{ value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" }, { value: "yearly", label: "Yearly" }]} />
        </Field>
        <div className="v2-form-row">
          <Field label="First invoice on"><input className="v2-input" type="date" value={f.startDate} onChange={set("startDate")} /></Field>
          <Field label="Ends">
            <Segmented size="sm" ariaLabel="End condition" value={ends} onChange={setEnds}
              options={[{ value: "never", label: "Never" }, { value: "date", label: "On date" }, { value: "count", label: "After…" }]} />
          </Field>
        </div>
        {ends === "date" && <Field label="Last invoice on or before"><input className="v2-input" type="date" min={f.startDate} value={f.endDate} onChange={set("endDate")} /></Field>}
        {ends === "count" && <Field label="Number of invoices"><input className="v2-input" type="number" min="1" value={f.maxOccurrences} onChange={set("maxOccurrences")} placeholder="e.g. 12" /></Field>}

        <div className="v2c-section-title" style={{ margin: "6px 0 0 4px" }}>Each invoice includes</div>
        <LineItems items={lines} onChange={setLines} products={products} productsLoading={pLoading} defaultGst={0} compact />

        <div className="v2-form-row">
          <Field label="Tax">
            <Segmented size="sm" ariaLabel="Tax type" value={f.taxType} onChange={(v) => setF((p) => ({ ...p, taxType: v }))}
              options={[{ value: "CGST_SGST", label: "CGST + SGST" }, { value: "IGST", label: "IGST" }]} />
          </Field>
          <Field label="Discount %"><input className="v2-input" type="number" min="0" max="100" value={f.discountPercent} onChange={set("discountPercent")} /></Field>
        </div>
        <div className="v2-form-row">
          <Field label="Invoice prefix"><input className="v2-input v2-mono" value={f.invoicePrefix} onChange={(e) => setF((p) => ({ ...p, invoicePrefix: e.target.value.toUpperCase() }))} /></Field>
          <Field label="Payment terms (days)"><input className="v2-input" type="number" min="0" value={f.paymentTimeline} onChange={set("paymentTimeline")} /></Field>
        </div>
        <div className="v2-inset">
          <Switch id="v2r-mail" checked={!!f.autoSendEmail} onChange={(v) => setF((p) => ({ ...p, autoSendEmail: v }))} label="Email each invoice automatically"
            description={client?.clientEmail ? `Sent to ${client.clientEmail}` : "The client needs an email address on file"} />
        </div>
        <Field label="Notes" optional><textarea className="v2-input v2-textarea" rows={2} value={f.notes} onChange={set("notes")} /></Field>
        <div className="v2-inset v2cn-totals">
          <div className="v2-inset-row v2-inset-row--static"><span>Subtotal</span><b>{inr(preview.subtotal, { decimals: 2 })}</b></div>
          {preview.discountAmount > 0 && <div className="v2-inset-row v2-inset-row--static"><span>Discount</span><b>−{inr(preview.discountAmount, { decimals: 2 })}</b></div>}
          <div className="v2-inset-row v2-inset-row--static"><span>GST</span><b>{inr(preview.totalGst, { decimals: 2 })}</b></div>
          <div className="v2-inset-row v2-inset-row--static v2pub-grand"><span>Per invoice</span><b>{inr(preview.grandTotal, { decimals: 2 })}</b></div>
        </div>
        {err && <div className="v2-banner v2-banner--bad" role="alert"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}
